<script lang="ts">
	import { A, CloseButton, Drawer, Hr, Img, Sidebar, SidebarGroup, SidebarItem, SidebarWrapper } from "flowbite-svelte";
	import { getContext, onMount } from "svelte";
	import { sineIn } from "svelte/easing";
	import { PaginationArgumentsSchema } from "#gen/common/v1/pagination_pb.js";
	import type { User } from "#gen/identities/v1/identities_pb.js";
	import type { AbacusClient, EventList } from "#lib/api/abacus.js";
	import { handleError } from "#lib/api/error.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import { Logger } from "#lib/logging.js";
	import type { ViewerNavigation } from "#lib/navigation.js";
	import { create } from "#lib/protobuf-compat.js";
	import { formatTimestamp, formatTimestampAsDate } from "#lib/timestamp.js";
	import type { Writable } from "svelte/store";

	interface Props {
		open: boolean;
		onLoad?: (suiteId: string) => void;
	}

	const abacusClient: AbacusClient = getContext("abacusClient");
	const navigation: ViewerNavigation = getContext("navigation");
	const user: Writable<User> = getContext("user");

	let { open = $bindable(false), onLoad }: Props = $props();
	let conversions: Promise<EventList> | undefined = $state();
	let hidden = $derived(!open);

	onMount(listConversions);

	function loadSuite(suiteId: string) {
		onLoad?.(suiteId);
	}

	async function listConversions() {
		if (!$user) {
			return;
		}
		try {
			conversions = abacusClient.listConversionsByConsumer($user.email, undefined, create(PaginationArgumentsSchema, { first: 5 }));
		} catch (e) {
			const err = handleError(e);
			Logger.error("Error listing conversions", { err: err ?? e });
		}
	}
</script>

<Drawer width="w-5/6" divClass="max-w-md z-50 p-4 bg-white rounded-l-xl border-l border-t border-b relative h-full" backdrop={false} position="absolute" placement="right" activateClickOutside={false} transitionType="fly" transitionParams={{ x: 320, duration: 200, easing: sineIn }} bind:hidden id="sidebar">
	<div class="flex h-full flex-col">
		<CloseButton on:click={() => (open = false)} class="mb-4 dark:hover:bg-gray-100" />
		<Sidebar asideClass="flex flex-col grow w-full h-full overflow-y-auto">
			{#await conversions then list}
				{#if $user && list && list.events.length > 0}
					{#each list.events as conversion (formatTimestamp(conversion.createdAt).replaceAll(" ", "-") + "-" + $user.id)}
						<A on:click={() => loadSuite(conversion.suiteId)} color="" class="rounded-lg px-2 py-3 transition duration-75 hover:bg-gray-100 hover:no-underline">
							<div class="flex items-start space-x-2">
								<div class="w-10 flex-none">
									<Img class="aspect-square rounded-lg border object-cover" src={conversion?.photoUrl} />
								</div>
								<div class="grow">
									<P class="text-gray-800 dark:text-gray-300" weight="bold" size="sm">{conversion.name}</P>
									<P class="text-gray-800 dark:text-gray-300" weight="light" size="sm">{formatTimestampAsDate(conversion.createdAt, $user.timezone)}</P>
								</div>
							</div>
						</A>
					{/each}
					<Hr hrClass="my-4 dark:bg-gray-200" />
				{/if}
			{/await}
			<SidebarWrapper divClass="overflow-y-auto rounded">
				{#each navigation.groups as group (group.items.join("-"))}
					<SidebarGroup>
						{#each group.items as item (item.href)}
							<SidebarItem class="text-gray-800 dark:text-gray-800 dark:hover:bg-gray-100" label={item.name} href={item.href}>
								<svelte:fragment slot="icon">
									<Icon color="black" name={item.icon} class="mr-2" />
								</svelte:fragment>
							</SidebarItem>
						{/each}
					</SidebarGroup>
				{/each}
			</SidebarWrapper>
		</Sidebar>
	</div>
</Drawer>
